import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Download, GripVertical, RotateCcw, Check } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { api } from '../api/client';
import { toast, Checkbox } from './UI';
import { COLUMN_LABELS, DEFAULT_TASK_COLUMNS } from './TaskTable';

function normalizeColumns(stored) {
  const knownIds = DEFAULT_TASK_COLUMNS.map(c => c.id);
  const safe = Array.isArray(stored) ? stored.filter(c => knownIds.includes(c.id)) : [];
  const haveIds = new Set(safe.map(c => c.id));
  for (const def of DEFAULT_TASK_COLUMNS) {
    if (!haveIds.has(def.id)) safe.push(def);
  }
  return safe;
}

// TagEditor as standalone component so it doesn't lose focus on every render
function TagEditor({ label, values, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const submit = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    onAdd(v);
    setDraft('');
  };
  return (
    <div>
      <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10, display: 'block' }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {values.map((val, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg-3)',
            border: '1px solid var(--border)', borderRadius: 20, padding: '4px 10px' }}>
            <span style={{ fontSize: 13 }}>{val}</span>
            <button onClick={() => onRemove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 0 }}>
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Neuen Wert hinzufügen..." />
        <button className="btn btn-ghost" onClick={submit}><Plus size={14} /></button>
      </div>
    </div>
  );
}

// Auswahl einer Teilmenge der Statuswerte. Bewusst kein TagEditor: die Werte
// muessen exakt zu status_values passen, freier Text waere hier eine Fehlerquelle.
function StatusPicker({ values, selected, onToggle }) {
  if (!values?.length) {
    return <p style={{ color: 'var(--text-3)', fontSize: 12 }}>Erst Status-Werte anlegen.</p>;
  }
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {values.map(v => {
        const active = (selected || []).includes(v);
        return (
          <button key={v} type="button" onClick={() => onToggle(v)}
            style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 'var(--radius)', cursor: 'pointer',
              border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              background: active ? 'var(--accent-dim)' : 'var(--bg-2)',
              color: active ? 'var(--accent)' : 'var(--text-2)',
            }}>
            {active ? '\u2713 ' : ''}{v}
          </button>
        );
      })}
    </div>
  );
}

export default function Settings() {
  const { settings, updateSettings } = useApp();
  const [form, setForm] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [draggedColIdx, setDraggedColIdx] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  const lastSavedRef = useRef(null);
  const saveTimerRef = useRef(null);
  const initializedRef = useRef(false);

  // Initial load — only once, never overwrites later user edits
  useEffect(() => {
    if (settings && !initializedRef.current) {
      const initial = {
        status_values: [...(settings.status_values || [])],
        categories: [...(settings.categories || [])],
        importance_values: [...(settings.importance_values || [])],
        urgency_values: [...(settings.urgency_values || [])],
        stale_threshold_days: settings.stale_threshold_days || 14,
        done_status_values: [...(settings.done_status_values || ['Done', 'Abgebrochen'])],
        waiting_status_values: [...(settings.waiting_status_values || ['Warten auf X'])],
        task_columns: normalizeColumns(settings.task_columns),
        default_sort: settings.default_sort || { column: 'created_at', dir: 'desc' },
        theme: settings.theme || 'dark',
      };
      setForm(initial);
      lastSavedRef.current = JSON.stringify(initial);
      initializedRef.current = true;
    }
  }, [settings]);

  // Auto-save with debounce
  useEffect(() => {
    if (!form) return;
    const current = JSON.stringify(form);
    if (current === lastSavedRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        await updateSettings(form);
        lastSavedRef.current = current;
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 1500);
      } catch (err) {
        setSaveStatus('error');
        toast(err.message, 'error');
      }
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [form, updateSettings]);

  if (!form) return <div style={{ padding: 24, color: 'var(--text-3)' }}>Laden...</div>;

  const addTo = (field) => (val) => setForm(f => ({ ...f, [field]: [...f[field], val] }));
  const removeFrom = (field) => (i) => setForm(f => ({ ...f, [field]: f[field].filter((_, j) => j !== i) }));
  const toggleIn = (field) => (val) => setForm(f => ({
    ...f,
    [field]: (f[field] || []).includes(val)
      ? f[field].filter(v => v !== val)
      : [...(f[field] || []), val],
  }));

  const toggleColumn = (idx) => {
    setForm(f => ({
      ...f,
      task_columns: f.task_columns.map((c, i) => i === idx ? { ...c, visible: !c.visible } : c),
    }));
  };
  const resetColumns = () => setForm(f => ({ ...f, task_columns: DEFAULT_TASK_COLUMNS.map(c => ({ ...c })) }));

  const onColDragStart = (idx) => () => setDraggedColIdx(idx);
  const onColDragOver = (idx) => (e) => {
    e.preventDefault();
    if (idx !== dragOverColIdx) setDragOverColIdx(idx);
  };
  const onColDragLeave = () => setDragOverColIdx(null);
  const onColDrop = (idx) => (e) => {
    e.preventDefault();
    setDragOverColIdx(null);
    if (draggedColIdx === null || draggedColIdx === idx) { setDraggedColIdx(null); return; }
    setForm(f => {
      const next = [...f.task_columns];
      const [moved] = next.splice(draggedColIdx, 1);
      next.splice(idx, 0, moved);
      return { ...f, task_columns: next };
    });
    setDraggedColIdx(null);
  };
  const onColDragEnd = () => { setDraggedColIdx(null); setDragOverColIdx(null); };

  const exportCsv = async () => {
    const blob = await api.exportTasksCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tasks.csv'; a.click();
  };

  const SaveIndicator = () => {
    if (saveStatus === 'saving') return <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Speichert…</span>;
    if (saveStatus === 'saved') return <span style={{ fontSize: 12, color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={12} /> Gespeichert</span>;
    if (saveStatus === 'error') return <span style={{ fontSize: 12, color: 'var(--red)' }}>Fehler beim Speichern</span>;
    return null;
  };

  return (
    <div style={{ padding: '24px', maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>Einstellungen</h1>
        <SaveIndicator />
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <TagEditor label="📊 Status-Werte" values={form.status_values}
          onAdd={addTo('status_values')} onRemove={removeFrom('status_values')} />
        <hr className="divider" />
        <TagEditor label="🏷 Kategorien" values={form.categories}
          onAdd={addTo('categories')} onRemove={removeFrom('categories')} />
        <hr className="divider" />
        <TagEditor label="⭐ Wichtigkeit" values={form.importance_values}
          onAdd={addTo('importance_values')} onRemove={removeFrom('importance_values')} />
        <hr className="divider" />
        <TagEditor label="⚡ Dringlichkeit" values={form.urgency_values}
          onAdd={addTo('urgency_values')} onRemove={removeFrom('urgency_values')} />
        <hr className="divider" />
        <div>
          <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 8, display: 'block' }}>
            ✅ Abgeschlossene Status
          </label>
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 10 }}>
            Diese Status gelten als erledigt: keine Inaktiv-Warnung, keine Überfällig-Markierung,
            und sie tauchen nicht mehr in den Task-Pools von Planung, Woche und Heute auf.
          </p>
          <StatusPicker values={form.status_values} selected={form.done_status_values}
            onToggle={toggleIn('done_status_values')} />
        </div>
        <hr className="divider" />
        <div>
          <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 8, display: 'block' }}>
            ⏸ Warte-Status
          </label>
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 10 }}>
            Bewusstes Warten ist kein Liegenbleiben — Tasks in diesen Status bekommen keine Inaktiv-Warnung.
          </p>
          <StatusPicker values={form.status_values} selected={form.waiting_status_values}
            onToggle={toggleIn('waiting_status_values')} />
        </div>
        <hr className="divider" />
        <div>
          <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 8, display: 'block' }}>
            ⚠️ Inaktiv-Warnung nach (Tagen)
          </label>
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 10 }}>
            Ein Task gilt als inaktiv, wenn seit so vielen Tagen nichts Inhaltliches mehr daran
            passiert ist — gezählt werden Titel, Status, nächster Schritt, Fälligkeit, Notizen,
            Größe, Verantwortlicher und Fortschritt. Reines Umsortieren (Parent, Ebene, Zeitraum,
            Kategorie) zählt nicht.
          </p>
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 10 }}>
            Ausgenommen sind: abgeschlossene und wartende Status, delegierte Tasks,
            Tasks mit Fälligkeit in der Zukunft sowie alle Ziel-Ebenen (Quartal, Monat, Woche, Key Result).
          </p>
          <input type="number" value={form.stale_threshold_days} min={1} max={365}
            onChange={e => setForm(f => ({ ...f, stale_threshold_days: +e.target.value }))}
            style={{ width: 100 }} />
        </div>
      </div>

      {/* Erscheinungsbild */}
      <div className="card">
        <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10, display: 'block' }}>
          🎨 Erscheinungsbild
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { value: 'dark', label: 'Dunkel', preview: { bg: '#161614', accent: '#D4A853', text: '#E8E8E0' } },
            { value: 'light', label: 'Hell (Pastell)', preview: { bg: '#FAF8F2', accent: '#C97B5A', text: '#2A2824' } },
          ].map(opt => {
            const active = form.theme === opt.value;
            return (
              <button key={opt.value}
                onClick={() => setForm(f => ({ ...f, theme: opt.value }))}
                style={{
                  flex: 1, padding: '12px 16px',
                  background: opt.preview.bg,
                  border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer', textAlign: 'left',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: opt.preview.accent, borderRadius: 4 }} />
                  <div style={{ width: 12, height: 12, background: opt.preview.text, borderRadius: 4, opacity: 0.3 }} />
                </div>
                <span style={{ fontSize: 13, color: opt.preview.text, fontWeight: active ? 500 : 400 }}>
                  {opt.label}{active ? ' ✓' : ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Standard-Sortierung */}
      <div className="card">
        <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 8, display: 'block' }}>
          🔀 Standard-Sortierung in Tasks
        </label>
        <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 10 }}>Wird beim Öffnen der Tasks-Ansicht angewendet. Klick auf einen Spalten-Header sortiert temporär.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={form.default_sort.column}
            onChange={e => setForm(f => ({ ...f, default_sort: { ...f.default_sort, column: e.target.value } }))}
            style={{ flex: 1 }}>
            <option value="created_at">Erstellt am</option>
            <option value="updated_at">Zuletzt geändert</option>
            <option value="title">Titel</option>
            <option value="status">Status</option>
            <option value="next_step_due_date">Fälligkeitsdatum</option>
            <option value="importance">Wichtigkeit</option>
            <option value="urgency">Dringlichkeit</option>
            <option value="owner">Bei wem</option>
          </select>
          <select value={form.default_sort.dir}
            onChange={e => setForm(f => ({ ...f, default_sort: { ...f.default_sort, dir: e.target.value } }))}
            style={{ width: 160 }}>
            <option value="asc">Aufsteigend</option>
            <option value="desc">Absteigend</option>
          </select>
        </div>
      </div>

      {/* Spalten-Verwaltung */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', flex: 1 }}>
            🗂 Spalten in der Task-Ansicht
          </label>
          <button className="btn btn-ghost btn-sm" onClick={resetColumns} title="Auf Standard zurücksetzen">
            <RotateCcw size={12} /> Standard
          </button>
        </div>
        <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 12 }}>
          Reihenfolge per Drag-and-Drop am Griff, Sichtbarkeit über Checkbox. Wird automatisch gespeichert.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {form.task_columns.map((col, idx) => {
            const isDragged = draggedColIdx === idx;
            const isOver = dragOverColIdx === idx && draggedColIdx !== null && draggedColIdx !== idx;
            return (
              <div key={col.id}
                draggable
                onDragStart={onColDragStart(idx)}
                onDragOver={onColDragOver(idx)}
                onDragLeave={onColDragLeave}
                onDrop={onColDrop(idx)}
                onDragEnd={onColDragEnd}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px',
                  background: 'var(--bg-2)',
                  border: '1px solid var(--border)',
                  borderTopColor: isOver ? 'var(--accent)' : 'var(--border)',
                  borderRadius: 'var(--radius)',
                  opacity: isDragged ? 0.4 : 1,
                  cursor: 'move',
                  userSelect: 'none',
                }}
              >
                <GripVertical size={14} color="var(--text-3)" />
                <span style={{ flex: 1, fontSize: 13, color: col.visible ? 'var(--text)' : 'var(--text-3)' }}>
                  {COLUMN_LABELS[col.id] || col.id}
                </span>
                <Checkbox checked={col.visible} onChange={() => toggleColumn(idx)} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10, display: 'block' }}>
          📤 Daten exportieren
        </label>
        <button className="btn btn-ghost" onClick={exportCsv}><Download size={14} /> Tasks als CSV exportieren</button>
      </div>
    </div>
  );
}
